import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Home as HomeIcon } from "lucide-react";
import { SettingsProvider } from "./context/SettingsContext";
import { PremiumProvider } from "./context/PremiumContext";
import { Toaster } from "./components/ui/sonner";
import { startReminderWatcher } from "./lib/reminders";
import { trackPageView } from "./lib/analytics";
import Home from "./pages/Home";
import Reader from "./pages/Reader";
import Devotionals from "./pages/Devotionals";
import ReadingPlans from "./pages/ReadingPlans";
import PrayerJournal from "./pages/PrayerJournal";
import Memorize from "./pages/Memorize";
import SermonNotes from "./pages/SermonNotes";
import Family from "./pages/Family";
import AudioBible from "./pages/AudioBible";
import Characters from "./pages/Characters";
import VerseOfDayPage from "./pages/VerseOfDayPage";
import Premium from "./pages/Premium";
import Privacy from "./pages/Privacy";
import AdminStats from "./pages/AdminStats";
import SavedVerses from "./pages/SavedVerses";
import ReadingJourney from "./pages/ReadingJourney";
import WordStudy from "./pages/WordStudy";
import Download from "./pages/Download";
import BibleSlider from "./components/BibleSlider";
import PremiumRoute from "./components/PremiumRoute";
import TranslationGuard from "./components/TranslationGuard";
import OnboardingPaywall from "./components/OnboardingPaywall";
import GuidedTour from "./components/GuidedTour";
import UpgradeDialog from "./components/UpgradeDialog";
import ReviewPrompt from "./components/ReviewPrompt";

// Seed the default translation (NLT) on first launch, before the settings
// provider reads it. NKJV is a premium translation.
try {
  if (localStorage.getItem("fw_translation") === null) {
    localStorage.setItem("fw_translation", JSON.stringify("NLT"));
  }
} catch { /* ignore */ }

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

// Persistent home button on every screen except Home and the reader
// (the reader already has its own home button).
function FloatingHome() {
  const { pathname } = useLocation();
  if (pathname === "/" || pathname.startsWith("/read/") || pathname === "/download") return null;
  return (
    <Link
      to="/"
      data-testid="floating-home"
      aria-label="Go to home"
      className="fixed bottom-24 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-primary/20 backdrop-blur-md transition-transform hover:scale-105 active:scale-95"
    >
      <HomeIcon className="h-5 w-5" />
    </Link>
  );
}

function App() {
  useEffect(() => {
    startReminderWatcher();
  }, []);
  return (
    <div className="App">
      <SettingsProvider>
        <PremiumProvider>
          <BrowserRouter>
            <RouteTracker />
            <TranslationGuard />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/read/:book/:chapter" element={<Reader />} />
              <Route path="/devotionals" element={<PremiumRoute feature="Daily Devotionals" perks={["Spurgeon's Morning & Evening", "7-day series per theme", "A fresh story & image every day"]}><Devotionals /></PremiumRoute>} />
              <Route path="/plans" element={<PremiumRoute feature="Reading Plans" perks={["5 guided plans", "Track your progress", "Gospels, Psalms, Proverbs & more"]}><ReadingPlans /></PremiumRoute>} />
              <Route path="/listen" element={<PremiumRoute feature="Audio Bible" perks={["Onyx studio narration", "Follow-along highlighting", "Every chapter"]}><AudioBible /></PremiumRoute>} />
              <Route path="/journal" element={<PremiumRoute feature="Prayer Journal" perks={["Private prayer entries", "Track answered prayers"]}><PrayerJournal /></PremiumRoute>} />
              <Route path="/memorize" element={<PremiumRoute feature="Verse Memorization" perks={["Save verses to memorize", "Practice & self-check"]}><Memorize /></PremiumRoute>} />
              <Route path="/notes" element={<PremiumRoute feature="Sermon Notes" perks={["Capture sermon notes", "Organized by date"]}><SermonNotes /></PremiumRoute>} />
              <Route path="/family" element={<PremiumRoute feature="Family Sharing" perks={["Private family group", "Shared prayer wall", "Pray for each other"]}><Family /></PremiumRoute>} />
              <Route path="/characters" element={<PremiumRoute feature="Character Cards" perks={["Explore Bible characters", "Their story & key verses"]}><Characters /></PremiumRoute>} />
              <Route path="/verse" element={<PremiumRoute feature="Verse of the Day" perks={["A fresh verse daily", "Beautiful shareable art"]}><VerseOfDayPage /></PremiumRoute>} />
              <Route path="/saved" element={<PremiumRoute feature="Saved Verses" perks={["Bookmark favorite verses", "Revisit anytime"]}><SavedVerses /></PremiumRoute>} />
              <Route path="/journey" element={<PremiumRoute feature="Reading Journey" perks={["See your reading history", "Bible coverage at a glance"]}><ReadingJourney /></PremiumRoute>} />
              <Route path="/word-study" element={<PremiumRoute feature="Word Study" perks={["Original Hebrew & Greek meaning", "Key verses for each word"]}><WordStudy /></PremiumRoute>} />
              <Route path="/download" element={<Download />} />
              <Route path="/get" element={<Download />} />
              <Route path="/premium" element={<Premium />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/admin/stats" element={<AdminStats />} />
            </Routes>
            <BibleSlider />
            <FloatingHome />
            <OnboardingPaywall />
            <GuidedTour />
            <UpgradeDialog />
            <ReviewPrompt />
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </PremiumProvider>
      </SettingsProvider>
    </div>
  );
}

export default App;
